// DiscountTable.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Input from "../../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/Dropdown";
import Button from "../../components/ui/Button";
import {
  Edit,
  MoreVertical,
  Trash2,
  Plus,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Eye,
} from "lucide-react";
import Swal from "sweetalert2";
import {
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/Card";
import PermissionGuard from "../../components/auth/PermissionGuard";

const API_BASE = "https://tyka.premierhostings.com/beta/api";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");

const formatType = (t) =>
  t ? t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "-";

const DiscountTable = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [discounts, setDiscounts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [meta, setMeta] = useState(null);
  const [viewDiscount, setViewDiscount] = useState(null);

  const token = localStorage.getItem("token");

  // URL is the single source of truth
  const currentPage = Number(searchParams.get("page")) || 1;
  const entriesPerPage = Number(searchParams.get("perPage")) || 10;
  const searchTerm = searchParams.get("search") || "";
  const sortBy = searchParams.get("sortBy") || "created_at";
  const sortDir = searchParams.get("sortDir") || "desc";

  // Local state only for the search box (debounced into the URL)
  const [searchInput, setSearchInput] = useState(searchTerm);

  const updateParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next);
    setSelectedIds([]);
  };

  const goToPage = (p) => updateParams({ page: p });

  const handleSort = (field) => {
    if (sortBy === field) {
      updateParams({ sortDir: sortDir === "asc" ? "desc" : "asc", page: 1 });
    } else {
      updateParams({ sortBy: field, sortDir: "asc", page: 1 });
    }
  };

  // Debounce search -> URL
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput.trim() !== searchTerm) {
        updateParams({ search: searchInput.trim(), page: 1 });
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Sync input when URL changes (back/forward button)
  useEffect(() => {
    setSearchInput((prev) => (prev.trim() === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  // Close view modal with Esc
  useEffect(() => {
    if (!viewDiscount) return;
    const onKey = (e) => {
      if (e.key === "Escape") setViewDiscount(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewDiscount]);

  const fetchDiscounts = async () => {
    try {
      const res = await axios.get(`${API_BASE}/discounts`, {
        params: {
          page: currentPage,
          perPage: entriesPerPage,
          search: searchTerm,
          sortBy,
          sortDir,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      const dataArray = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      setDiscounts(dataArray);
      setMeta(res.data?.meta ?? null);
    } catch (error) {
      console.error("Error fetching discounts:", error);
      setDiscounts([]);
      setMeta(null);
    }
  };

  useEffect(() => {
    fetchDiscounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, entriesPerPage, searchTerm, sortBy, sortDir]);

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this discount!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${API_BASE}/discounts/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          Swal.fire("Deleted!", "Discount has been deleted.", "success");

          // Last row of a page > 1 deleted -> go to previous page
          if (discounts.length === 1 && currentPage > 1) {
            goToPage(currentPage - 1);
          } else {
            fetchDiscounts();
          }
        } catch (error) {
          console.error("Delete Error:", error);
          Swal.fire("Error!", "Failed to delete discount.", "error");
        }
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      Swal.fire("No selection", "Please select at least one discount.", "info");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: `You want to delete ${selectedIds.length} discount(s)!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete them!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(
            `${API_BASE}/discount/bulk-delete`,
            { ids: selectedIds },
            { headers: { Authorization: `Bearer ${token}` } },
          );

          Swal.fire(
            "Deleted!",
            `${selectedIds.length} discount(s) deleted.`,
            "success",
          );

          const deletedWholePage = selectedIds.length >= discounts.length;
          setSelectedIds([]);

          if (deletedWholePage && currentPage > 1) {
            goToPage(currentPage - 1);
          } else {
            fetchDiscounts();
          }
        } catch (error) {
          console.error("Bulk Delete Error:", error);
          Swal.fire("Error!", "Failed to delete discounts.", "error");
        }
      }
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === discounts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(discounts.map((d) => d.id));
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    }
    return sortDir === "asc" ? (
      <ArrowUp className="h-3 w-3" />
    ) : (
      <ArrowDown className="h-3 w-3" />
    );
  };

  // ---------- View modal ----------
  const renderViewModal = () => {
    if (!viewDiscount) return null;

    const app = viewDiscount.applications?.[0];

    const rows = [
      ["Name", viewDiscount.name],
      [
        "Value",
        viewDiscount.type === "percentage"
          ? `${viewDiscount.amount}%`
          : `₹${viewDiscount.amount}`,
      ],
      ["Discount Type", formatType(viewDiscount.type)],
      ["Applies To", formatType(app?.applicable_type)],
      [
        "Target",
        app?.applicable_type === "product"
          ? "All Products"
          : (app?.applicable_data?.name ?? app?.applicable_id ?? "-"),
      ],
      ["Start Date", formatDate(viewDiscount.start_date)],
      ["End Date", formatDate(viewDiscount.end_date)],
    ];

    return (
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
        onClick={() => setViewDiscount(null)}
      >
        <div
          className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800 dark:text-white"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-4 flex items-center justify-between border-b pb-3 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-red-600">
              Discount Details
            </h2>
            <button
              type="button"
              onClick={() => setViewDiscount(null)}
              className="text-2xl leading-none text-gray-500 hover:text-gray-800 dark:hover:text-white"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <dl className="max-h-[60vh] space-y-2 overflow-y-auto">
            {rows.map(([label, value]) => (
              <div key={label} className="flex justify-between">
                <dt className="font-medium text-gray-600 dark:text-gray-300">
                  {label}
                </dt>
                <dd className="text-right font-semibold">{value}</dd>
              </div>
            ))}

            <div className="flex justify-between gap-4">
              <dt className="font-medium text-gray-600 dark:text-gray-300">
                Status
              </dt>
              <dd>
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                    viewDiscount.active
                      ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                      : "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
                  }`}
                >
                  {viewDiscount.active ? "Active" : "Inactive"}
                </span>
              </dd>
            </div>
          </dl>

          <div className="mt-6 flex justify-end gap-3 border-t pt-4 dark:border-gray-700">
            <Button variant="outline" onClick={() => setViewDiscount(null)}>
              Close
            </Button>
            <PermissionGuard permission="edit_discounts">
              <Button
                onClick={() => {
                  const id = viewDiscount.id;
                  setViewDiscount(null);
                  navigate(`/discounts/edit/${id}`);
                }}
              >
                Edit
              </Button>
            </PermissionGuard>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <CardHeader className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 w-full justify-between items-center">
          <div className="flex flex-col space-y-2 sm:flex-1">
            <CardTitle className="text-xl sm:text-xl">Discounts</CardTitle>
            <CardDescription className="text-sm">
              Manage your product discounts
            </CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {selectedIds.length > 0 && (
              <PermissionGuard permission="bulk_delete_discounts">
                <Button
                  className="flex-shrink-0 w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Bulk Delete ({selectedIds.length})
                </Button>
              </PermissionGuard>
            )}
            <PermissionGuard permission="create_brands">
              <Button
                className="flex-shrink-0 w-full sm:w-auto bg-red-500 hover:bg-red-800 text-white mr-0"
                onClick={() => navigate("/discounts/add")}
              >
                <Plus className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Add Discount</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </PermissionGuard>
          </div>
        </div>
      </CardHeader>

      {/* Table Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
        <div className="flex items-center gap-2 text-sm ml-6">
          <label
            htmlFor="usersPerPage"
            className="text-lg font-medium text-gray-700"
          >
            Show
          </label>
          <select
            id="usersPerPage"
            value={entriesPerPage}
            onChange={(e) => updateParams({ perPage: e.target.value, page: 1 })}
            className="border rounded p-1 dark:bg-gray-700"
          >
            {[10, 20, 50, 100].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
          <span className="text-lg text-gray-700 font-semibold">entries</span>
        </div>

        <div className="flex items-center px-3 py-2 flex-1 max-w-xs sm:max-w-sm">
          <Input
            placeholder="Search discount..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="border-none p-0 ml-2 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 w-full min-w-0"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table className="min-w-[900px] w-full px-2">
          <TableHeader>
            <TableRow className="text-center">
              <TableHead className="text-center">
                <input
                  type="checkbox"
                  onChange={toggleSelectAll}
                  checked={
                    discounts.length > 0 &&
                    selectedIds.length === discounts.length
                  }
                />
              </TableHead>
              <TableHead className="text-center">
                <button
                  type="button"
                  onClick={() => handleSort("name")}
                  className="inline-flex items-center gap-1"
                >
                  Discount Name <SortIcon field="name" />
                </button>
              </TableHead>
              <TableHead className="text-center">
                <button
                  type="button"
                  onClick={() => handleSort("amount")}
                  className="inline-flex items-center gap-1"
                >
                  Discount Value <SortIcon field="amount" />
                </button>
              </TableHead>
              <TableHead className="text-center">
                <button
                  type="button"
                  onClick={() => handleSort("start_date")}
                  className="inline-flex items-center gap-1"
                >
                  Start Date <span className="mx-1 text-gray-500">→</span> End
                  Date
                  <SortIcon field="start_date" />
                </button>
              </TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {discounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">
                  No discounts found.
                </TableCell>
              </TableRow>
            ) : (
              discounts.map((discount) => {
                const applicableType =
                  discount.applications?.[0]?.applicable_type;

                return (
                  <TableRow key={discount.id}>
                    <TableCell className="text-center">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(discount.id)}
                        onChange={() => toggleSelect(discount.id)}
                      />
                    </TableCell>

                    <TableCell className="text-center">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <span>{discount.name}</span>

                        {applicableType?.trim() && (
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                              applicableType === "product"
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200"
                                : "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200"
                            }`}
                          >
                            {formatType(applicableType)}
                          </span>
                        )}
                      </div>
                    </TableCell>

                    <TableCell className="text-center">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white ">
                          {discount.type === "percentage"
                            ? `${discount.amount}%`
                            : `₹${discount.amount}`}
                        </span>

                        <span
                          className={`inline-flex items-center justify-center w-fit mx-auto px-2.5 py-1 rounded-full text-xs font-medium uppercase ${
                            discount.type?.toLowerCase() === "percentage"
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                              : "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"
                          }`}
                        >
                          {discount.type}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell className="text-center">
                      {discount.start_date && discount.end_date ? (
                        <span className="text-sm">
                          {formatDate(discount.start_date)}{" "}
                          <span className="mx-1 text-gray-500">→</span>
                          {formatDate(discount.end_date)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </TableCell>

                    <TableCell className="text-center">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          discount.active
                            ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
                        }`}
                      >
                        {discount.active ? "Active" : "Inactive"}
                      </span>
                    </TableCell>

                    <TableCell className="text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                          align="end"
                          sideOffset={5}
                          className="z-[9999] w-36 bg-white border-2 rounded-md"
                        >
                          <DropdownMenuItem
                            onClick={() => setViewDiscount(discount)}
                            className="flex items-center justify-center py-1 text-blue-500 text-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <Eye className="mr-2 h-4 w-4" /> View
                          </DropdownMenuItem>

                          <PermissionGuard permission="edit_discounts">
                            <DropdownMenuItem
                              onClick={() =>
                                navigate(`/discounts/edit/${discount.id}`)
                              }
                              className="flex items-center justify-center py-1 text-yellow-500 text-lg border focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                            >
                              <Edit className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                          </PermissionGuard>

                          <PermissionGuard permission="delete_discounts">
                            <DropdownMenuItem
                              onClick={() => handleDelete(discount.id)}
                              className="flex items-center justify-center py-1 text-lg border text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                            >
                              <Trash2 className="mr-2 h-4 w-4 " /> Delete
                            </DropdownMenuItem>
                          </PermissionGuard>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {meta && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-2 mt-2 border-t-2 border-b-2">
          <div className="text-sm md:text-base font-semibold text-gray-600 dark:text-white ml-3">
            Showing {meta.from ?? 0} to {meta.to ?? 0} of {meta.total ?? 0}{" "}
            entries
          </div>

          <div className="flex justify-between items-center mt-4 gap-2">
            <Button
              disabled={meta.current_page === 1}
              onClick={() => goToPage(meta.current_page - 1)}
              className="bg-red-500 text-white cursor-pointer"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>

            {/* First Page */}
            <Button
              size="sm"
              variant={meta.current_page === 1 ? "default" : "outline"}
              onClick={() => goToPage(1)}
            >
              1
            </Button>

            {/* Left ellipsis */}
            {meta.current_page > 3 && <span className="px-2">...</span>}

            {/* Middle Pages */}
            {Array.from({ length: 3 }, (_, i) => {
              const pageNumber = meta.current_page - 1 + i;
              if (pageNumber > 1 && pageNumber < meta.last_page) {
                return (
                  <Button
                    key={pageNumber}
                    size="sm"
                    variant={
                      meta.current_page === pageNumber ? "default" : "outline"
                    }
                    onClick={() => goToPage(pageNumber)}
                  >
                    {pageNumber}
                  </Button>
                );
              }
              return null;
            })}

            {/* Right ellipsis */}
            {meta.current_page < meta.last_page - 2 && (
              <span className="px-2">...</span>
            )}

            {/* Last Page */}
            {meta.last_page > 1 && (
              <Button
                size="sm"
                variant={
                  meta.current_page === meta.last_page ? "default" : "outline"
                }
                onClick={() => goToPage(meta.last_page)}
              >
                {meta.last_page}
              </Button>
            )}

            <Button
              disabled={meta.current_page === meta.last_page}
              onClick={() => goToPage(meta.current_page + 1)}
              className="bg-red-500 text-white cursor-pointer"
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* View Modal */}
      {renderViewModal()}
    </div>
  );
};

export default DiscountTable;
